<template>
    <div class="bg-cover bg-center bg-no-repeat p-1 rounded-lg flex flex-col" :style="{ backgroundImage: `url(${InsightsBackgroundImage})` }">
        <div class="bg-white p-3 rounded-lg flex flex-col flex-1">
            <div class="flex items-center justify-between gap-2 w-full mb-2">
                <div class="flex items-center gap-2">
                    <i class="bi bi-stars gray-900 text-sm" aria-hidden="true"></i>
                    <p class="details gray-900 text-left font-light">{{ t("insights.calendar.cardTitle") }}</p>
                </div>
                <BaseTooltip side="top" :trigger-aria-label="t('insights.calendar.tooltipTriggerLabel')">
                    <template #trigger>
                        <span class="inline-flex text-ink-muted hover:text-ink body-small shrink-0" :aria-label="insightTooltipContent">
                            <i class="bi bi-question-circle" aria-hidden="true"></i>
                        </span>
                    </template>
                    <template #content>{{ insightTooltipContent }}</template>
                </BaseTooltip>
            </div>
            <div class="flex flex-col items-start gap-2 w-full flex-1">
                <div class="w-full">
                    <h3 class="nadia-subtitle text-ink text-left">{{ displayTitle }}</h3>
                </div>
                <div class="w-full border-t border-border my-2"></div>
                <p class="body-small font-light grow flex text-pretty">{{ displayBody }}</p>
                <div v-if="showCtaButton" class="flex flex-col items-center w-full mt-auto">
                    <BaseButton
                        data-test-id="dive-deeper-btn"
                        :theme="tonalThemeEnabled ? 'primary' : 'secondary'"
                        class="w-full mt-2 flex items-center justify-center"
                        :class="{ 'body-small rounded-3xl': !tonalThemeEnabled }"
                        :disabled="ctaDisabled || ctaLoading"
                        @click="handleCtaClick()">
                        <LoadingSpinner v-if="ctaLoading" class="h-4 shrink-0" />
                        <span v-else>{{ ctaBtnText ?? t("insights.calendar.diveDeeper") }}</span>
                    </BaseButton>
                </div>
                <div v-if="showFeedbackButtons || showConfidenceIndicator" class="w-full border-t border-border mt-2 pt-3 flex items-center justify-between">
                    <ConfidenceIndicator v-if="showConfidenceIndicator" :level="displayConfidenceLevel" />
                    <span v-else />
                    <FeedbackButtons
                        v-if="showFeedbackButtons"
                        :value="feedbackData?.feedback?.value ?? null"
                        :disabled="feedbackSubmitting || feedbackFetching"
                        :initial-comment="feedbackData?.feedback?.comment ?? null"
                        :initial-issues="feedbackData?.feedback?.tags ?? null"
                        @select="handleFeedbackSelect"
                        @negative-feedback="handleNegativeFeedback" />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import InsightsBackgroundImage from "~img/card_backgrounds/analytics-insight.jpg";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import { useLabInsightFeedback } from "~vue/composables/useLabInsightFeedback";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import ConfidenceIndicator from "~vue/Labs/ConfidenceIndicator.vue";
import FeedbackButtons from "~vue/Labs/FeedbackButtons.vue";
import { CalendarInsightSchema } from "~vue/types/calendar_integration";
import { tonalThemeEnabled } from "~vue/utils/theme";
import { computed, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import type { z } from "zod";

interface Props {
    insightData: z.infer<typeof CalendarInsightSchema>;
    showCtaButton?: boolean;
    ctaBtnText?: string;
    ctaDisabled?: boolean;
    ctaLoading?: boolean;
    showConfidenceIndicator?: boolean;
    showFeedbackButtons?: boolean;
}

const { t } = useI18n();

const props = withDefaults(defineProps<Props>(), {
    showCtaButton: true,
    ctaLoading: false,
    ctaDisabled: false,
    showConfidenceIndicator: false,
    showFeedbackButtons: true,
});

// TODO: Remove the legacy calendar branch once the tonal theme becomes the default.

const emit = defineEmits<{
    (e: "cta-button-click"): void;
}>();

const displayTitle = computed(() => props.insightData.title);
const displayBody = computed(() => props.insightData.summary);
const displayConfidenceLevel = computed(() => props.insightData.confidence_level);

const { fetchFeedback, feedbackData, feedbackSubmitting, feedbackFetching, submitFeedback } = useLabInsightFeedback(props.insightData.insight_id);

onMounted(async () => {
    await fetchFeedback();
});

async function handleFeedbackSelect(value: 0 | 1) {
    await submitFeedback({ insight_id: props.insightData.insight_id, value, comment: null, tags: [] });
}

async function handleNegativeFeedback(payload: { comment?: string | null; issues?: string[] | null }) {
    const insightId = props.insightData.insight_id;
    await submitFeedback({ insight_id: insightId, value: 0, comment: payload.comment ?? null, tags: payload.issues ?? [] });
}

const handleCtaClick = () => {
    emit("cta-button-click");
};

const insightTooltipContent = computed(() => t("insights.calendar.tooltip"));
</script>
